import { createFocusTrap } from 'focus-trap';
import Overlay from 'utils/overlay';
import { transitionEnd, repaint } from 'utils/node';

type Events = 'open' | 'opened' | 'close' | 'closed';

export default class {
  public isActive: boolean;

  private readonly container: HTMLElement;

  private overlay: Overlay;

  private overlayElement: HTMLElement;

  private focusTrap;

  protected classes: { active: string };

  private closeTrigger: string;

  constructor(element: string | HTMLElement, overlayElement: HTMLElement = document.body) {
    this.container = typeof element === 'string'
      ? document.querySelector(element)
      : element;

    if (!(this.container instanceof HTMLElement)) {
      throw new TypeError(typeof element === 'string'
        ? `Could not find element matching selector: ${element}`
        : `HTMLElement was not passed to constructor. Instead ${element} was passed`);
    }

    this.isActive = false;
    this.container.hidden = true;

    this.overlayElement = overlayElement;
    this.overlay = new Overlay(this.overlayElement);

    this.closeTrigger = 'data-close';

    this.focusTrap = createFocusTrap(this.container, {
      allowOutsideClick: true,
    });

    this.clickEvent = this.clickEvent.bind(this);
    this.keydownEvent = this.keydownEvent.bind(this);
    this.addEventListeners = this.addEventListeners.bind(this);
  }

  private addEventListeners(): void {
    document.addEventListener('touchstart', this.clickEvent);
    document.addEventListener('click', this.clickEvent);
    document.addEventListener('keydown', this.keydownEvent);
    this.focusTrap.activate();
  }

  private removeEventListeners(): void {
    document.removeEventListener('touchstart', this.clickEvent);
    document.removeEventListener('click', this.clickEvent);
    document.removeEventListener('keydown', this.keydownEvent);
    this.focusTrap.deactivate();
  }

  private clickEvent(event: MouseEvent | TouchEvent): void {
    const target = event.target as HTMLElement;

    if (!(event.target instanceof Node)) {
      return;
    }

    if (target === this.overlay.element) {
      event.stopPropagation();
      this.close();
    }

    if (target.hasAttribute(this.closeTrigger)) {
      const value = target.getAttribute(this.closeTrigger);

      if (value !== '') {
        const relative = target.closest('[aria-modal="true"]');

        if (this.container.id === relative.id) {
          event.stopPropagation();
          this.close();
        }
      } else if (this.container.contains(target)) {
        event.stopPropagation();
        this.close();
      }
    }
  }

  private keydownEvent(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  toggle(): void {
    if (this.isActive) {
      this.close();
    } else {
      this.open();
    }
  }

  async open(): Promise<void> {
    if (this.isActive) {
      return;
    }

    this.dispatchEvent('open');

    this.isActive = true;
    this.container.hidden = false;
    repaint(this.container);

    this.container.classList.add(this.classes.active);

    const { disablePageScroll } = (await import(
      /* webpackChunkName: "scroll-lock" */
      'modules/scroll-lock'
    ));

    disablePageScroll([
      this.container,
      ...this.container.querySelectorAll('[data-scrollable]'),
    ]);

    this.overlay.show();

    setTimeout(() => {
      this.addEventListeners();
    }, 0);

    this.dispatchEvent('opened');
  }

  async close(): Promise<void> {
    if (this.isActive === false) {
      return;
    }

    this.dispatchEvent('close');

    this.isActive = false;
    this.container.classList.remove(this.classes.active);

    const { enablePageScroll } = (await import(
      /* webpackChunkName: "scroll-lock" */
      'modules/scroll-lock'
    ));

    enablePageScroll([
      this.container,
      ...this.container.querySelectorAll('[data-scrollable]'),
    ]);

    this.overlay.hide();

    this.removeEventListeners();

    transitionEnd(this.container, () => {
      if (this.isActive === false) {
        this.container.hidden = true;
        this.dispatchEvent('closed');
      }
    }, { once: true });
  }

  private dispatchEvent(type: Events): void {
    const event = new Event(type);

    this.container.dispatchEvent(event);
  }

  on(type: Events, callback: CallableFunction): void {
    this.container.addEventListener(type, (event) => callback(event));
  }
}
